import { StrictMode, Suspense, lazy, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes, useParams, useLocation } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useMe, WorkspaceProvider } from "./lib/session";
import { RealtimeProvider } from "./lib/realtime";
import { AppShell } from "./app/AppShell";
import { ToastProvider, Spinner, EmptyState } from "./ui/kit";
import { ApiRequestError } from "./lib/api";
import "./styles/app.css";
import "./styles/app-extra.css";

const Home = lazy(() => import("./pages/marketing/Home"));
const Pricing = lazy(() => import("./pages/marketing/Pricing"));
const Portal = lazy(() => import("./pages/portal/Portal"));
const Login = lazy(() => import("./pages/auth/Login"));
const Signup = lazy(() => import("./pages/auth/Signup"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const VerifyEmail = lazy(() => import("./pages/auth/VerifyEmail"));
const AcceptInvite = lazy(() => import("./pages/auth/AcceptInvite"));
const NewWorkspace = lazy(() => import("./pages/auth/NewWorkspace"));
const MfaSetup = lazy(() => import("./pages/auth/MfaSetup"));
const Onboarding = lazy(() => import("./pages/onboarding/Onboarding"));
const Inbox = lazy(() => import("./pages/inbox/Inbox"));
const AiAgent = lazy(() => import("./pages/ai/AiAgent"));
const Knowledge = lazy(() => import("./pages/knowledge/Knowledge"));
const Customers = lazy(() => import("./pages/customers/Customers"));
const Analytics = lazy(() => import("./pages/analytics/Analytics"));
const Automations = lazy(() => import("./pages/automations/Automations"));
const Integrations = lazy(() => import("./pages/integrations/Integrations"));
const Settings = lazy(() => import("./pages/settings/Settings"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, err) => !(err instanceof ApiRequestError && err.status < 500) && count < 2,
      refetchOnWindowFocus: false,
    },
  },
});

function Loading() {
  return (
    <div style={{ display: "grid", placeItems: "center", minHeight: "60vh" }}>
      <Spinner />
    </div>
  );
}

/** Redirects to /login when signed out. */
function RequireAuth({ children }: { children: ReactNode }) {
  const me = useMe();
  const location = useLocation();
  if (me.isPending) return <Loading />;
  if (!me.data) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return <>{children}</>;
}

/** /app -> first workspace (onboarding if unfinished). */
function AppIndex() {
  const me = useMe();
  if (me.isPending) return <Loading />;
  const first = me.data?.workspaces[0];
  if (!first) return <Navigate to="/app/new" replace />;
  const o = first.onboarding;
  const done = o.completed === true || (o.currentStep === undefined && o.knowledgeImported && o.aiConfigured && o.widgetInstalled && o.testConversation);
  return <Navigate to={`/app/${first.id}/${done ? "inbox" : "onboarding"}`} replace />;
}

function WorkspaceRoutes() {
  const { wid } = useParams();
  const me = useMe();
  if (me.isPending || !me.data) return <Loading />;
  const ws = me.data.workspaces.find((w) => w.id === wid);
  if (!ws) {
    return (
      <EmptyState icon="alert" title="Workspace not found" action={<a className="btn" href="/app">Go to your workspaces</a>}>
        You might not be a member of this workspace anymore.
      </EmptyState>
    );
  }
  const value = { workspace: ws, role: ws.role, me: me.data, isAdmin: ws.role !== "agent" };
  return (
    <WorkspaceProvider value={value}>
      <RealtimeProvider workspaceId={ws.id}>
        <Routes>
          <Route path="onboarding" element={<Onboarding />} />
          <Route
            path="*"
            element={
              <AppShell>
                <Suspense fallback={<Loading />}>
                  <Routes>
                    <Route path="inbox" element={<Inbox />} />
                    <Route path="inbox/:conversationId" element={<Inbox />} />
                    <Route path="ai/*" element={<AiAgent />} />
                    <Route path="knowledge/*" element={<Knowledge />} />
                    <Route path="customers/*" element={<Customers />} />
                    <Route path="analytics" element={<Analytics />} />
                    <Route path="automations/*" element={<Automations />} />
                    <Route path="integrations/*" element={<Integrations />} />
                    <Route path="settings/*" element={<Settings />} />
                    <Route path="*" element={<Navigate to="inbox" replace />} />
                  </Routes>
                </Suspense>
              </AppShell>
            }
          />
        </Routes>
      </RealtimeProvider>
    </WorkspaceProvider>
  );
}

function App() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/help/:slug/*" element={<Portal />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/invite/:token" element={<AcceptInvite />} />
        <Route path="/app" element={<RequireAuth><AppIndex /></RequireAuth>} />
        <Route path="/mfa" element={<RequireAuth><MfaSetup /></RequireAuth>} />
        <Route path="/app/new" element={<RequireAuth><NewWorkspace /></RequireAuth>} />
        <Route path="/app/:wid/*" element={<RequireAuth><WorkspaceRoutes /></RequireAuth>} />
        <Route path="*" element={<EmptyState icon="alert" title="Page not found" action={<a className="btn" href="/">Go home</a>} />} />
      </Routes>
    </Suspense>
  );
}

const dark = localStorage.getItem("ap-theme") === "dark";

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <div id="ap-root" className={`ap${dark ? " dark" : ""}`}>
          <ToastProvider>
            <App />
          </ToastProvider>
        </div>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
